/**
 * The permission authority — the single source of truth for "may this user do X".
 *
 * ## Invariant: the JWT can only GRANT, never DENY.
 *
 * api.core embeds one `action` claim per permitted code (`TokenService.CreateToken`),
 * unioned across every role the user holds. That is the same claim the backend's
 * `HasActionHandler` reads, so sourcing the frontend gate from it means the two tiers
 * cannot disagree about what a user may do.
 *
 * But a role holding zero actions emits *no* `action` claims at all — which is
 * byte-identical to a token minted before the claim shipped. We cannot tell those
 * apart, so `readActionClaims` returns `null` for both and the caller falls back to
 * the API. A genuinely permissionless user therefore pays one request; in exchange, a
 * bug in this decoder can never empty every gate in the app at once. That trade is
 * the whole reason this file is safe to depend on.
 *
 * ## Claim shapes
 *
 * .NET serializes repeated claims as a bare string when there is exactly one and a
 * JSON array when there is more than one. Both `role` and `action` are affected, so
 * every read here normalizes through `toStringArray`.
 */

import jwtDecode from 'jwt-decode';

import { USERDATACOOKIE } from '@/constants/global';
import type { ACTIONTYPE } from '@/layout/permissions/actions';

import { EMPTY_SNAPSHOT, type IPermissionSnapshot } from './types';

/** The claim key api.core writes one entry per permitted action into. */
const ACTION_CLAIM = 'action';
const ROLE_CLAIM = 'role';

/** The outer envelope written by `encodeData` — wraps the real backend token. */
interface IEnvelope {
	token?: string;
}

/** Only the claims this module reads; the token carries many more. */
interface IClaims {
	[ACTION_CLAIM]?: string | string[];
	[ROLE_CLAIM]?: string | string[];
}

const toStringArray = (value: string | string[] | undefined): string[] => {
	if (!value) return [];
	if (typeof value === 'string') return [value];
	return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
};

const decode = <T>(token: string): T | null => {
	try {
		return jwtDecode<T>(token);
	} catch {
		return null;
	}
};

/**
 * Read the `action` claims off a raw backend token.
 *
 * Returns `null` when the claim key is absent — meaning either a legacy token or a
 * genuinely permissionless role. Callers must treat `null` as "ask the API", never as
 * "this user holds nothing". Returns a (possibly empty) array only when the key exists.
 */
export const readActionClaims = (token: string): string[] | null => {
	const claims = decode<IClaims>(token);
	if (!claims || !(ACTION_CLAIM in claims)) return null;
	return toStringArray(claims[ACTION_CLAIM]);
};

export const readRoleClaims = (token: string): string[] => {
	const claims = decode<IClaims>(token);
	if (!claims) return [];
	return toStringArray(claims[ROLE_CLAIM]);
};

/**
 * Backend codes are lowercase snake_case, but the legacy seed generation contains
 * mixed-case names (`EC8A_viewer`). Lowercasing both sides is what lets a role holding
 * a legacy grant still match the catalogue.
 */
export const buildActionSet = (codes: readonly string[]): ReadonlySet<ACTIONTYPE> =>
	new Set<ACTIONTYPE>(codes.map((code) => code.toLowerCase() as ACTIONTYPE));

/** Reads the stored envelope. Returns '' when nothing is stored or storage is blocked. */
export const readStoredEnvelope = (): string => {
	try {
		return localStorage.getItem(USERDATACOOKIE) ?? '';
	} catch {
		return '';
	}
};

let memoKey: string | null = null;
let memoValue: IPermissionSnapshot = EMPTY_SNAPSHOT;

/**
 * Build a snapshot from the currently stored token. Memoized on the raw envelope
 * string so repeated calls return an identical object — `useSyncExternalStore`
 * requires a stable reference or it re-renders forever.
 */
export const getJwtSnapshot = (): IPermissionSnapshot => {
	const envelope = readStoredEnvelope();

	if (memoKey === envelope) return memoValue;
	memoKey = envelope;

	if (!envelope) {
		memoValue = EMPTY_SNAPSHOT;
		return memoValue;
	}

	const outer = decode<IEnvelope>(envelope);
	const token = outer?.token;

	if (!token) {
		memoValue = EMPTY_SNAPSHOT;
		return memoValue;
	}

	const roles = readRoleClaims(token);
	const codes = readActionClaims(token);

	// `null` — no `action` key on the token. Unresolved, so the caller fetches.
	if (codes === null) {
		memoValue = { ...EMPTY_SNAPSHOT, roles };
		return memoValue;
	}

	memoValue = {
		actions: buildActionSet(codes),
		roles,
		source: 'jwt',
		isResolved: true,
	};

	return memoValue;
};

/**
 * Build a resolved snapshot from a plain list of codes. For tests, stories and the
 * "view as role" preview — anywhere the permission set comes from something other than
 * the signed-in user's own token.
 */
export const makeSnapshot = (codes: readonly string[], roles: readonly string[] = []): IPermissionSnapshot => ({
	actions: buildActionSet(codes),
	roles,
	source: 'api',
	isResolved: true,
});

/** Test seam — drops the memo so a test can swap the stored token between cases. */
export const resetSnapshotCache = () => {
	memoKey = null;
	memoValue = EMPTY_SNAPSHOT;
};

export const has = (snapshot: IPermissionSnapshot, action: ACTIONTYPE): boolean => snapshot.actions.has(action);

export const hasAny = (snapshot: IPermissionSnapshot, actions: readonly ACTIONTYPE[]): boolean =>
	actions.some((action) => snapshot.actions.has(action));

export const hasAll = (snapshot: IPermissionSnapshot, actions: readonly ACTIONTYPE[]): boolean =>
	actions.length > 0 && actions.every((action) => snapshot.actions.has(action));

/**
 * Role membership read live from the snapshot rather than the module-scope
 * `ISSUPERADMIN` constant in `constants/global.ts`, which is evaluated once at import
 * and is therefore stale after any login that does not reload the page.
 */
export const isSuperAdminRoles = (roles: readonly string[]): boolean =>
	roles.some((role) => (role ?? '').trim().toLowerCase() === 'super admin');

export const isSuperAdmin = (snapshot: IPermissionSnapshot): boolean => isSuperAdminRoles(snapshot.roles);

/**
 * Whether this role list belongs to an observer organization's own admin.
 *
 * Super Admin wins when both are held: roles are a union, and the observer dashboard is scoped
 * to the ONE partner the token names — a Super Admin proxies for any partner through Manage
 * Observers instead. Takes the raw list so callers holding `getUserData()` roles rather than a
 * snapshot (the Overview redirect, the access-denied home link) share the one rule.
 */
export const isObserverAdminRoles = (roles: readonly string[]): boolean => {
	const normalized = roles.map((role) => (role ?? '').trim().toLowerCase());
	return normalized.includes('observer admin') && !normalized.includes('super admin');
};

export const isObserverAdmin = (snapshot: IPermissionSnapshot): boolean => isObserverAdminRoles(snapshot.roles);

/** True when the snapshot holds any of `roles` (case- and whitespace-insensitive). */
export const hasAnyRole = (snapshot: IPermissionSnapshot, roles: readonly string[]): boolean => {
	const held = new Set(snapshot.roles.map((role) => (role ?? '').trim().toLowerCase()));
	return roles.some((role) => held.has(role.trim().toLowerCase()));
};
